import React, { useCallback, useState } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  LegendType,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { barGraphConfig } from "../../../constants/constants";

type BarData = {
  [key: string]: string;
};

type Props = {
  barData: BarData[];
  expanded?: boolean;
};

const OverallAnalysisGraph: React.FC<Props> = ({
  barData,
  expanded = false,
}) => {
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const TriangleBar = useCallback((props: any) => {
    const { fill, x, y, width, height } = props;
    const path = `M${x},${y + height}C${x + width / 3},${y + height} ${x + width / 2},${y + height / 3}
      ${x + width / 2}, ${y}
      C${x + width / 2},${y + height / 3} ${x + (2 * width) / 3},${y + height}
      ${x + width}, ${y + height}Z`;
    return <path d={path} stroke="none" fill={fill} />;
  }, []);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const CustomTooltip = (props: any) => {
    const { active, payload, label } = props;
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div
          className="border-4 rounded bg-gray-400 py-3"
          style={{ backgroundColor: "#d5dbd6" }}
        >
          <p className="text-secondary text-xs">
            <span className="font-semibold">Trend Score: </span>
            {Number(data["Trend Score"]).toFixed(2)} <br />
            <span className="font-semibold">Bet:</span> {data.bet} <br />
            <span className="font-semibold">Impact:</span> {data.impact}
          </p>
        </div>
      );
    }

    return null;
  };

  const legendPayload = barGraphConfig.map(config => ({
    value: config.bet,
    type: "square" as LegendType,
    color: config.color,
  }));

  const maxTrendScore = barData
    ? barData.length > 0
      ? Math.ceil(Number(barData[0]["Trend Score"]))
      : 0
    : 0;

  return (
    <div className="font-poppins">
      <BarChart
        width={1200}
        height={expanded ? 350 : 280}
        data={barData}
        margin={{
          top: 20,
          bottom: 20,
        }}
        barGap={expanded ? 2 : 1}
      >
        <CartesianGrid strokeDasharray="3 3" />
        <XAxis
          dataKey="tNo"
          width={1}
          tick={{ fontSize: expanded ? 14 : 12 }}
          tickFormatter={(value, index) => (index % 2 === 0 ? value : "")}
          label={{
            value: "Trend Rank",
            position: "insideBottom",
            offset: expanded ? -20 : -10,
            fill: "#000000",
          }}
          axisLine={{ stroke: "#000000" }}
        />
        <YAxis
          tick={{ fontSize: expanded ? 14 : 12 }}
          axisLine={{ stroke: "#000000" }}
          domain={[0, maxTrendScore + 20]}
          label={{
            value: "Popularity",
            angle: -90,
            position: "insideLeft",
            offset: expanded ? 10 : 20,
            dy: 40,
            fill: "#000000",
          }}
        />
        <Tooltip content={<CustomTooltip />} />
        {expanded && (
          <Legend
            payload={legendPayload}
            align="right"
            verticalAlign="top"
            wrapperStyle={{ paddingBottom: "20px" }}
          />
        )}
        <Bar
          dataKey="Trend Score"
          fill="#8884d8"
          shape={<TriangleBar />}
          barSize={25}
          label={
            expanded
              ? {
                  position: "top",
                  fontSize: 14,
                  angle: -90,
                  dy: -10,
                  dx: 5,
                  formatter: (value: number) => Number(value).toFixed(2),
                }
              : false
          }
        >
          {barData.map((entry, index) => (
            <Cell key={`cell-${index}`} fill={entry.graphColour} />
          ))}
        </Bar>
      </BarChart>
    </div>
  );
};

export default OverallAnalysisGraph;
